import { motion } from "motion/react";
import { Radar, Database, Zap } from "lucide-react";
import { useEffect, useState } from "react";

export function HeroMatrix() {
  const [radarAngle, setRadarAngle] = useState(0);
  const [detectedPoints, setDetectedPoints] = useState<{ x: number; y: number; id: number }[]>([]);

  useEffect(() => {
    const interval = setInterval(() => {
      setRadarAngle((prev) => (prev + 2) % 360);
      
      // Adiciona pontos detectados aleatoriamente
      if (Math.random() > 0.7) {
        const newPoint = {
          x: Math.random() * 300 - 150,
          y: Math.random() * 300 - 150,
          id: Date.now()
        };
        setDetectedPoints((prev) => [...prev.slice(-20), newPoint]);
      }
    }, 50);

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Background Grid animado */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" 
          style={{
            backgroundImage: `
              linear-gradient(to right, #00F5FF 1px, transparent 1px),
              linear-gradient(to bottom, #00F5FF 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px'
          }}
        />
      </div>

      {/* Scan lines */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'repeating-linear-gradient(0deg, rgba(0,245,255,0.03) 0px, transparent 2px, transparent 4px, rgba(0,245,255,0.03) 6px)'
        }}
        animate={{ y: ['-100%', '100%'] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
      />

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Coluna Esquerda - Hero Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#00F5FF]/30 bg-[#00F5FF]/5"
            >
              <Zap className="w-4 h-4 text-[#00F5FF]" />
              <span className="text-sm text-[#00F5FF] font-mono">PhD Level Analytics System</span>
            </motion.div>

            {/* Headline */}
            <div className="space-y-4">
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-5xl md:text-7xl font-bold text-white leading-tight"
              >
                WP Total
                <span className="block text-[#00F5FF]">Tracker</span>
              </motion.h1>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-xl md:text-2xl text-gray-300"
              >
                Sistema Forense de Rastreamento para WordPress
              </motion.p>
            </div>

            {/* Features List */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="space-y-3"
            >
              {[
                'Identity Resolution V32 - Rastreie pessoas, não sessões',
                'GPS Comportamental - Mapeie cada movimento do visitante',
                'Bot Killer System - Elimine 99.7% de tráfego falso'
              ].map((feature, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#00F5FF] mt-2 flex-shrink-0" />
                  <p className="text-gray-300">{feature}</p>
                </div>
              ))}
            </motion.div>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex flex-wrap gap-4"
            >
              <button className="px-8 py-4 bg-[#FF4D00] hover:bg-[#FF4D00]/90 text-white rounded-lg font-bold transition-all hover:scale-105 hover:shadow-lg hover:shadow-[#FF4D00]/50">
                Iniciar Rastreamento Agora
              </button>
              <button className="px-8 py-4 border border-[#00F5FF] text-[#00F5FF] hover:bg-[#00F5FF]/10 rounded-lg font-bold transition-all">
                Ver Demo Ao Vivo
              </button>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="grid grid-cols-3 gap-6 pt-8 border-t border-[#00F5FF]/20"
            >
              {[
                { label: 'Sites Ativos', value: '500+' },
                { label: 'Leads Rastreados', value: '1.2M+' },
                { label: 'Uptime', value: '99.9%' }
              ].map((stat, idx) => (
                <div key={idx}>
                  <div className="text-2xl font-bold text-[#00F5FF] font-mono">{stat.value}</div>
                  <div className="text-sm text-gray-400">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Coluna Direita - Radar GPS Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative"
          >
            {/* Container do Radar */}
            <div className="relative w-full max-w-[500px] mx-auto aspect-square">
              {/* Background circles */}
              <div className="absolute inset-0 flex items-center justify-center">
                {[1, 2, 3, 4].map((circle) => (
                  <motion.div
                    key={circle}
                    className="absolute rounded-full border border-[#00F5FF]/20"
                    style={{
                      width: `${circle * 25}%`,
                      height: `${circle * 25}%`
                    }}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: circle * 0.1, duration: 0.5 }}
                  />
                ))}
              </div>

              {/* Linhas de grade */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="absolute w-full h-0.5 bg-gradient-to-r from-transparent via-[#00F5FF]/20 to-transparent" />
                <div className="absolute w-0.5 h-full bg-gradient-to-b from-transparent via-[#00F5FF]/20 to-transparent" />
              </div>

              {/* Radar sweep — leque conic com rastro fosforoso (igual plugin PHP) */}
              <motion.div
                className="absolute inset-0 flex items-center justify-center pointer-events-none"
                style={{ rotate: radarAngle }}
              >
                <div
                  className="absolute rounded-full"
                  style={{
                    width: '100%',
                    height: '100%',
                    background: 'conic-gradient(from 270deg, transparent 0%, transparent 200deg, rgba(0,245,255,0.05) 240deg, rgba(0,245,255,0.2) 300deg, rgba(0,245,255,0.8) 350deg, rgba(0,245,255,1) 360deg)',
                    filter: 'drop-shadow(0 0 8px rgba(0,245,255,0.8))',
                    mixBlendMode: 'screen',
                  }}
                />
              </motion.div>

              {/* Silhuetas humanas detectadas */}
              {detectedPoints.map((point) => (
                <motion.div
                  key={point.id}
                  className="absolute"
                  style={{
                    left: `calc(50% + ${point.x}px - 7px)`,
                    top: `calc(50% + ${point.y}px - 9px)`
                  }}
                  initial={{ scale: 0, opacity: 1 }}
                  animate={{ scale: [1, 1.3, 1], opacity: [1, 0.6, 0] }}
                  transition={{ duration: 2.5 }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="18"
                    viewBox="0 0 14 18"
                    fill="#00F5FF"
                    style={{ filter: 'drop-shadow(0 0 6px rgba(0,245,255,0.8)) drop-shadow(0 0 12px rgba(0,245,255,0.4))' }}
                  >
                    {/* Cabeça */}
                    <circle cx="7" cy="4.5" r="3.5" />
                    {/* Ombros + tronco (curva, sem pescoço blocado) */}
                    <ellipse cx="7" cy="14" rx="6" ry="5" />
                  </svg>
                </motion.div>
              ))}

              {/* Centro do radar */}
              <div className="absolute inset-0 flex items-center justify-center">
                <motion.div
                  className="w-20 h-20 rounded-full border-2 border-[#00F5FF] bg-[#00F5FF]/10 flex items-center justify-center"
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Radar className="w-10 h-10 text-[#00F5FF]" />
                </motion.div>
              </div>

              {/* Labels nos cantos */}
              <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-[#00F5FF] text-sm font-mono">
                N
              </div>
              <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[#00F5FF] text-sm font-mono">
                S
              </div>
              <div className="absolute top-1/2 -translate-y-1/2 -left-6 text-[#00F5FF] text-sm font-mono">
                W
              </div>
              <div className="absolute top-1/2 -translate-y-1/2 -right-6 text-[#00F5FF] text-sm font-mono">
                E
              </div>
            </div>

            {/* Status Panel */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="mt-8 p-6 rounded-xl border border-[#00F5FF]/30 bg-gradient-to-br from-[#00F5FF]/5 to-transparent backdrop-blur-sm"
            >
              <div className="flex items-center gap-3 mb-4">
                <Database className="w-5 h-5 text-[#00F5FF]" />
                <span className="font-mono text-[#00F5FF] text-sm">SYSTEM STATUS</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-gray-400 mb-1">Active Scans</div>
                  <div className="text-lg font-bold text-white font-mono">{detectedPoints.length}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400 mb-1">Detection Rate</div>
                  <div className="text-lg font-bold text-[#10B981] font-mono">99.7%</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
